// The portal's navigation model, in one place.
//
// Two levels: a top-level rail picks a `Destination`, and the section nav lists
// that destination's `sections`. Keeping this declarative means adding a screen
// later is a data change plus a component, not a rewrite of the shell.
//
// Paths are grouped by *purpose* rather than flat, so the URL says what kind of
// page it is: `/docs/*` is public reading, `/dashboard/*` needs a credential.
// The root is the landing page and belongs to neither.

import type { DocTreeId } from '../docs/manifest.ts'

export type DestinationId = 'home' | 'docs' | 'dashboard'

export interface Section {
  id: string
  label: string
  path: string
  /** Rendered as a muted tag beside the label, e.g. "Beta". */
  tag?: string
  /** Requires a signed-in caller; the shell prompts for sign-in instead. */
  authenticated?: boolean
  /** Not built yet — renders the "coming soon" placeholder. */
  placeholder?: boolean
  /**
   * Listed but not navigable from the rail: the row renders muted with no link.
   * The path itself stays live for anyone holding a direct URL — this hides the
   * door, it does not lock it.
   */
  disabled?: boolean
}

export interface Destination {
  id: DestinationId
  label: string
  /** Phosphor icon name, resolved in Rail.tsx. */
  icon: 'House' | 'BookOpen' | 'ChartLine'
  sections: Section[]
}

export const DESTINATIONS: readonly Destination[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'House',
    sections: [{ id: 'home', label: 'Overview', path: '/' }],
  },
  {
    id: 'docs',
    label: 'Docs',
    icon: 'BookOpen',
    sections: [
      // GraphQL first: it is the current partner API, and the group's first
      // section is where a bare `/docs` (or a click on the group row) lands.
      { id: 'graphql-docs', label: 'GraphQL', path: '/docs/graphql' },
      // `webhooks-docs`, not `webhooks`: that id belongs to the dashboard
      // section, and the shell's gating special-case keys on it (App.tsx).
      { id: 'webhooks-docs', label: 'Webhooks', path: '/docs/webhooks' },
      {
        id: 'reference',
        label: 'REST API',
        path: '/docs/api',
        // The REST API is not GA yet. The row stays visible so the rail tells
        // the whole story, but it does not navigate; `/docs/api` itself keeps
        // rendering for partners already in the beta holding a direct link.
        tag: 'Beta',
        disabled: true,
      },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'ChartLine',
    sections: [
      {
        id: 'bookings',
        label: 'Bookings',
        path: '/dashboard/bookings',
        authenticated: true,
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        path: '/dashboard/webhooks',
        authenticated: true,
      },
      {
        id: 'credentials',
        label: 'API keys & OAuth',
        path: '/dashboard/credentials',
        authenticated: true,
      },
    ],
  },
]

export const ALL_SECTIONS: readonly Section[] = DESTINATIONS.flatMap(
  destination => destination.sections
)

const HOME = ALL_SECTIONS[0]

export const GRAPHQL_DOCS_SECTION: Section =
  ALL_SECTIONS.find(section => section.id === 'graphql-docs') ?? HOME

export const WEBHOOKS_DOCS_SECTION: Section =
  ALL_SECTIONS.find(section => section.id === 'webhooks-docs') ?? HOME

/**
 * The section a documentation tree that has no rail entry resolves to.
 *
 * Eclipse and DSP are reachable by URL and deliberately absent from the nav, so
 * they need a section for the shell to render against but must not light up a
 * rail row. Its id matches no destination, which is exactly what leaves the rail
 * with nothing selected.
 */
export const UNLISTED_DOCS_SECTION: Section = {
  id: 'docs-unlisted',
  label: 'Documentation',
  path: '/docs',
}

// A `Record` rather than a lookup with a fallback so that adding a doc tree
// without deciding its rail treatment is a compile error, not a silently
// unhighlighted rail.
const SECTION_BY_TREE: Record<DocTreeId, Section> = {
  graphql: GRAPHQL_DOCS_SECTION,
  webhooks: WEBHOOKS_DOCS_SECTION,
  eclipse: UNLISTED_DOCS_SECTION,
  dsp: UNLISTED_DOCS_SECTION,
  sso: UNLISTED_DOCS_SECTION,
}

/** The rail section a documentation tree renders under. */
export const sectionForDocTree = (id: DocTreeId): Section =>
  SECTION_BY_TREE[id]

// Trailing slashes are equivalent to a browser and to the service's SPA
// fallback, so `/docs/api/` must resolve like `/docs/api` rather than falling
// through to the landing page.
const normalize = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

/** The section a pathname resolves to; anything unrecognised is the landing page. */
export const sectionForPath = (pathname: string): Section => {
  const path = normalize(pathname)

  const exact = ALL_SECTIONS.find(section => section.path === path)
  if (exact) return exact

  // A bare group — someone typing `/docs` or trimming a URL back to it — opens
  // that group's first page rather than bouncing to the landing page.
  const group = DESTINATIONS.find(
    destination => path === `/${destination.id}`
  )
  if (group) return group.sections[0]

  return HOME
}

export const destinationForSection = (section: Section): Destination => {
  const owner = DESTINATIONS.find(destination =>
    destination.sections.some(candidate => candidate.id === section.id)
  )
  if (owner) return owner

  // Documentation with no rail entry — Eclipse, DSP, the terms page — still
  // belongs under Docs. Falling through to the default lit up *Home* while the
  // reader was on an Eclipse page, which is worse than lighting up nothing: the
  // rail asserted a location the reader was not at.
  if (section.id === UNLISTED_DOCS_SECTION.id) {
    return (
      DESTINATIONS.find(destination => destination.id === 'docs') ??
      DESTINATIONS[0]
    )
  }

  return DESTINATIONS[0]
}
